/**
 * DTemplate Problem Plot
 * The quality scores of the blueprint's household apps and of the AI
 * assistants, as two distributions on one axis
 */

import React from 'react';
import styled from 'styled-components';
import { HOUSE_H, HOUSE_PATH, HOUSE_W } from '@components/wee/house-mark';
import { colors } from '@config/colors';
import { typography } from '@config/typography';

/*
 * June, 2026-10-13: a plot on the Problem slide, "'Quality' as one axis and
 * Generic to Specialized as another", the April deck's Problem apps and the
 * AI assistants, logos for markers, a click opening why each sits where it
 * does, "with precise links to specific items". "Quality is for the job it
 * is doing, how well is it doing? We want to show the gap in how general AI
 * solutions are not providing quality solutions at the level of the
 * individualized apps." The Home CEO "at high quality/generic with a
 * *target asterix". Then, rapid edits: specialized on the left, no names
 * under the icons, circles, "Quality" and "Use Case" for the axes, every
 * icon where its numbers put it ("Yelp should be closer to generic than
 * Zillow"), close together where they are; Lemonade, Alexa, Copilot, Anna
 * and Ohai out.
 *
 * Each point is two numbers, both on its card:
 * - Quality, 0 to 100. An app built for one job: its US App Store rating
 *   ×20, unrounded, from Apple's lookup API on 2026-10-13. An AI assistant:
 *   its score in the newest independent test of its answers or actions
 *   found (their store ratings, 4.7 to 4.9 for most, measure general use,
 *   not a household job; none of the tests is a household job either).
 *   Instinct has neither a listing nor a published test, so it isn't here.
 * - Use case, 1 to 10: how many of a household's jobs it's for, from its own
 *   listing. 1 is one job (Zillow: a home); 10 is anything (a chatbot).
 *
 * Icons that would cover one another are nudged sideways (at most a third
 * of a step), never up or down: quality is exact, use case nearly so.
 */

type Plotted = {
  name: string;
  /** Its icon: public/press/problem/<slug>.png. */
  slug: string;
  /** Use case, 1 (one job) to 10 (anything). */
  useCase: number;
  /** Quality, 0 to 100. */
  quality: number;
  /** What it's for, as its listing puts it. */
  job: string;
  /** Where the quality comes from, linked. */
  source: React.ReactNode;
};

/** A link out of the deck, to the item it cites. */
const Cite: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer">
    {children}
  </a>
);

/** An App Store rating as the source of a quality score. */
const store = (rating: string, count: string, href: string) => (
  <>
    its {rating}★ rating, ×20, from {count} ratings on the <Cite href={href}>App Store</Cite> (Oct 2026).
  </>
);

/** The April deck's apps, each built for one household job. */
const SPECIALIZED: readonly Plotted[] = [
  { name: 'Zillow', slug: 'zillow', useCase: 1, quality: 96.0, job: 'search homes to buy or rent', source: store('4.80', '7.2M', 'https://apps.apple.com/us/app/zillow-real-estate-rentals/id310738695') },
  { name: 'OpenTable', slug: 'opentable', useCase: 1, quality: 98.4, job: 'reserve a table', source: store('4.92', '5.6M', 'https://apps.apple.com/us/app/opentable/id296581815') },
  { name: 'Niche', slug: 'niche', useCase: 1, quality: 82.3, job: 'pick a college', source: store('4.12', '1.4K', 'https://apps.apple.com/us/app/niche-college-search/id1356697847') },
  { name: 'UrbanSitter', slug: 'urbansitter', useCase: 1, quality: 95.7, job: 'book a babysitter neighbors trust', source: store('4.79', '15K', 'https://apps.apple.com/us/app/urbansitter-find-child-care/id548126716') },
  { name: 'Ring', slug: 'ring', useCase: 1, quality: 94.5, job: 'watch the home through its doorbells and cameras', source: store('4.72', '1.9M', 'https://apps.apple.com/us/app/ring-always-home/id926252661') },
  { name: 'ClassDojo', slug: 'classdojo', useCase: 1, quality: 97.0, job: 'hear from the kids’ teachers', source: store('4.85', '4.1M', 'https://apps.apple.com/us/app/classdojo/id552602056') },
  { name: 'CARFAX', slug: 'carfax', useCase: 2, quality: 97.1, job: 'buy a car, and see its history', source: store('4.85', '371K', 'https://apps.apple.com/us/app/carfax-new-used-cars/id479267592') },
  { name: 'Rover', slug: 'rover', useCase: 2, quality: 97.7, job: 'book pet sitters and dog walkers', source: store('4.89', '516K', 'https://apps.apple.com/us/app/rover-pet-sitters/id547320928') },
  { name: 'GEICO', slug: 'geico', useCase: 2, quality: 96.1, job: 'car insurance, and a tow when it’s needed', source: store('4.80', '4.8M', 'https://apps.apple.com/us/app/geico-mobile-car-insurance/id331763096') },
  { name: 'Chewy', slug: 'chewy', useCase: 2, quality: 98.2, job: 'pet food and supplies, and the pet pharmacy', source: store('4.91', '1.2M', 'https://apps.apple.com/us/app/chewy-pet-care-pharmacy/id1149449468') },
  { name: 'Instacart', slug: 'instacart', useCase: 2, quality: 96.4, job: 'groceries and household goods, delivered', source: store('4.82', '4.8M', 'https://apps.apple.com/us/app/instacart-groceries-food/id545599256') },
  { name: 'AAA', slug: 'aaa', useCase: 3, quality: 95.8, job: 'roadside help, trip planning and member discounts', source: store('4.79', '384K', 'https://apps.apple.com/us/app/aaa-mobile/id310730297') },
  { name: 'Khan Academy', slug: 'khanacademy', useCase: 3, quality: 91.4, job: 'learn any school subject', source: store('4.57', '112K', 'https://apps.apple.com/us/app/khan-academy/id469863705') },
  { name: 'Thumbtack', slug: 'thumbtack', useCase: 4, quality: 97.8, job: 'hire a pro for any home project', source: store('4.89', '474K', 'https://apps.apple.com/us/app/thumbtack-home-service-pros/id852703300') },
  { name: 'Care.com', slug: 'carecom', useCase: 4, quality: 87.9, job: 'care for kids, seniors and pets, and help at home', source: store('4.40', '43K', 'https://apps.apple.com/us/app/care-com-hire-caregivers/id328453619') },
  { name: 'Cozi', slug: 'cozi', useCase: 5, quality: 96.1, job: 'the family’s calendar, lists, reminders and meals', source: store('4.81', '401K', 'https://apps.apple.com/us/app/cozi-family-organizer/id407108860') },
  { name: 'Yelp', slug: 'yelp', useCase: 6, quality: 90.8, job: 'any local business: food, shops and services', source: store('4.54', '2.1M', 'https://apps.apple.com/us/app/yelp-food-services-reviews/id284910350') },
];

const WHICH =
  'https://www.which.co.uk/policy-and-insight/article/chatgpt-and-gemini-among-ai-tools-giving-risky-consumer-advice-which-finds-aBnBP0l2CE0T';

/** The AI assistants, each scored by an independent test (checked
 *  2026-10-13, every link loading; Which? scored ChatGPT and Gemini on the
 *  same 40 consumer questions). */
const ASSISTANTS: readonly Plotted[] = [
  {
    name: 'ChatGPT',
    slug: 'chatgpt',
    useCase: 10,
    quality: 64,
    job: 'anything, by text or voice',
    source: (
      <>
        experts’ score for its answers to 40 consumer questions (<Cite href={WHICH}>Which?</Cite>, Nov 2025).
      </>
    ),
  },
  {
    name: 'Gemini',
    slug: 'gemini',
    useCase: 10,
    quality: 69,
    job: 'anything, tied into Google’s apps',
    source: (
      <>
        experts’ score for its answers to 40 consumer questions (<Cite href={WHICH}>Which?</Cite>, Nov 2025).
      </>
    ),
  },
  {
    name: 'Claude',
    slug: 'claude',
    useCase: 10,
    quality: 90.4,
    job: 'anything: writing, research and analysis',
    source: (
      <>
        right on 90.4% of same-day news questions, 46% when one held a false premise (
        <Cite href="https://arxiv.org/abs/2605.22785">Suzgun et al., Stanford</Cite>, May 2026).
      </>
    ),
  },
  {
    name: 'Siri',
    slug: 'siri',
    useCase: 9,
    quality: 89.6,
    job: 'anything across Apple’s apps and her data',
    source: (
      <>
        passed 112 of <Cite href="https://pogueman.substack.com/p/125-tests-of-the-new-ai-siri">David Pogue’s 125 tests</Cite>{' '}
        outright (Sep 2026); its new app rates 3.4★ on the{' '}
        <Cite href="https://apps.apple.com/us/app/id6758482875">App Store</Cite>.
      </>
    ),
  },
  {
    name: 'Muse',
    slug: 'muse',
    useCase: 10,
    quality: 0,
    job: 'anything, acting across her apps and accounts',
    source: (
      <>
        none of 3 test orders went through (
        <Cite href="https://www.pymnts.com/news/artificial-intelligence/2026/meta-muse-cannot-order-pizza-without-help/">PYMNTS</Cite>,
        Sep 2026); it gave a Marketplace buyer the seller’s address (
        <Cite href="https://thenextweb.com/news/meta-muse-facebook-marketplace-address-buyer-robb">The Next Web</Cite>).
      </>
    ),
  },
];

/** Ours, the target: the quality of the apps built for one job, across all of them. */
const TARGET_QUALITY = 98;

// ── The bars ────────────────────────────────────────────────────────────────

/*
 * June, 2026-10-18: "show the distribution of quality scores for
 * specialized v generic AI as 2 different bar charts of the distributions,
 * where the background dark brand and light brand bars have the logos
 * stacked … the goal is to show how specialized solutions have higher user
 * satisfaction scores", The Home CEO's target "in a bar of it's own for the
 * generic category above the quality of the other".
 *
 * The two charts share one axis: bins of five points from 60 up, and
 * everything under 60 in one (Muse's 0 is the only score there). Each bar
 * holds its apps' logos two to a row, so a bar's height is its count.
 */
const EDGES = [0, 60, 65, 70, 75, 80, 85, 90, 95, 100];
const BINS = EDGES.slice(0, -1).map((lo, i) => ({ lo, hi: EDGES[i + 1] }));
const binOf = (quality: number) =>
  BINS.findIndex((bin, i) => quality >= bin.lo && (quality < bin.hi || i === BINS.length - 1));
const binned = (apps: readonly Plotted[]) => BINS.map((_, i) => apps.filter((p) => binOf(p.quality) === i));

const median = (values: readonly number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/** Our mark on the seafoam the app's icon wears. */
const HouseIcon: React.FC = () => (
  <HouseTile>
    <svg viewBox={`0 0 ${HOUSE_W} ${HOUSE_H}`} aria-hidden="true" focusable="false">
      <path d={HOUSE_PATH} fill={colors.brand.exxxtraDark} />
    </svg>
  </HouseTile>
);

const Bars: React.FC<{ apps: readonly Plotted[][]; tone: 'dark' | 'light'; target?: number }> = ({ apps, tone, target }) => (
  <Chart>
    {apps.map((inBin, i) => (
      <Slot key={BINS[i].lo}>
        {i === target ? (
          <>
            <TargetLabel>*Target</TargetLabel>
            <Bar $tone="target" title="The Home CEO*: the target, the quality of the apps built for one job, across all of them">
              <HouseIcon />
            </Bar>
          </>
        ) : (
          inBin.length > 0 && (
            <>
              <Count $tone={tone}>{inBin.length}</Count>
              <Bar $tone={tone}>
                {inBin.map((p) => (
                  <Icon key={p.name} src={`/press/problem/${p.slug}.png`} alt={p.name} title={`${p.name}: ${Math.round(p.quality)}/100`} />
                ))}
              </Bar>
            </>
          )
        )}
      </Slot>
    ))}
  </Chart>
);

export const ProblemPlot: React.FC = () => (
  <Plot>
    <ChartHead>
      <Swatch $tone="dark" />
      <b>Specialized apps</b>
      <Median>median {Math.round(median(SPECIALIZED.map((p) => p.quality)))}</Median>
      <Source>App Store rating ×20</Source>
    </ChartHead>
    <Bars apps={binned(SPECIALIZED)} tone="dark" />
    <ChartHead>
      <Swatch $tone="light" />
      <b>Generic AI</b>
      <Median>median {Math.round(median(ASSISTANTS.map((p) => p.quality)))}</Median>
      <Source>independent tests</Source>
    </ChartHead>
    <Bars apps={binned(ASSISTANTS)} tone="light" target={binOf(TARGET_QUALITY)} />
    <Axis>
      {BINS.map((bin) => (
        <span key={bin.lo}>
          {bin.lo}–{bin.hi}
        </span>
      ))}
    </Axis>
    <AxisTitle>Quality score</AxisTitle>
  </Plot>
);

/* Above the banner below it. */
const Plot = styled.div`
  position: relative;
  z-index: 2;
`;

const ChartHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ${typography.fontFamily.small};
  font-size: 15px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};

  b {
    font-weight: 700;
  }

  & + * {
    margin-top: 6px;
  }

  * + & {
    margin-top: 14px;
  }
`;

const Swatch = styled.span<{ $tone: 'dark' | 'light' }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: ${(p) => (p.$tone === 'dark' ? colors.brand.exxxxtraDark : colors.brand.primaryDark)};
`;

const Median = styled.span`
  padding: 2px 10px;
  border-radius: 9999px;
  background: ${colors.white};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: none;
  color: ${colors.accent.coral.exxxtraDark};
`;

const Source = styled.span`
  margin-left: auto;
  letter-spacing: 0.02em;
  text-transform: none;
  color: ${colors.highlight.light};
`;

const Chart = styled.div`
  display: grid;
  grid-template-columns: repeat(${BINS.length}, 1fr);
  align-items: end;
  column-gap: 10px;
  border-bottom: 2px solid ${colors.highlight.extraLight};
`;

const Slot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
`;

const Count = styled.span<{ $tone: 'dark' | 'light' }>`
  margin-bottom: 3px;
  font-family: ${typography.fontFamily.small};
  font-size: 14px;
  font-weight: 700;
  color: ${colors.brand.exxxtraDark};
`;

/* Two logos a row, filled from the bottom. */
const Bar = styled.div<{ $tone: 'dark' | 'light' | 'target' }>`
  width: 84px;
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap-reverse;
  justify-content: center;
  gap: 5px;
  padding: 6px;
  border-radius: 12px 12px 0 0;
  background: ${(p) =>
    p.$tone === 'dark' ? colors.brand.exxxxtraDark : p.$tone === 'light' ? colors.brand.primaryDark : colors.white};
  border: ${(p) => (p.$tone === 'target' ? `3px solid ${colors.accent.coral.exxxtraDark}` : 'none')};
  border-bottom: none;
`;

const Icon = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid ${colors.white};
  background: ${colors.white};
  box-sizing: border-box;
  object-fit: cover;
`;

const HouseTile = styled.span`
  display: flex;
  width: 32px;
  height: 32px;
  padding: 6px;
  box-sizing: border-box;
  border-radius: 50%;
  background: ${colors.brand.primaryDark};

  svg {
    width: 100%;
    height: 100%;
  }
`;

const TargetLabel = styled.span`
  margin-bottom: 3px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 14px;
  font-style: italic;
  color: ${colors.accent.coral.exxxtraDark};
  white-space: nowrap;
`;

const Axis = styled.div`
  display: grid;
  grid-template-columns: repeat(${BINS.length}, 1fr);
  column-gap: 10px;
  margin-top: 6px;
  text-align: center;
  font-family: ${typography.fontFamily.small};
  font-size: 13px;
  font-weight: 600;
  color: ${colors.highlight.light};
`;

const AxisTitle = styled.div`
  margin-top: 4px;
  text-align: center;
  font-family: ${typography.fontFamily.small};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
`;
